import { FormEvent, useEffect, useMemo, useState } from "react";
import { CalendarClock, CheckCircle2, Clock, Mail, MessageSquarePlus, Phone, Plus, RefreshCw, Save, Search, UserRound, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminApiError,
  AdminUser,
  CustomerServiceActivity,
  CustomerServiceActivityType,
  CustomerServiceAssignee,
  CustomerServiceSourceOption,
  CustomerServiceSourceType,
  CustomerServiceTicket,
  CustomerServiceTicketCategory,
  CustomerServiceTicketPayload,
  CustomerServiceTicketPriority,
  CustomerServiceTicketStatus,
  createCustomerServiceActivity,
  createCustomerServiceTicket,
  getCurrentAdmin,
  getCustomerServiceTicket,
  listCustomerServiceAssignees,
  listCustomerServiceTickets,
  logout,
  searchCustomerServiceSourceOptions,
  updateCustomerServiceTicket,
} from "./api";
import Pagination from "./components/Pagination";

const statusLabels: Record<CustomerServiceTicketStatus, string> = {
  New: "新工單",
  InProgress: "處理中",
  WaitingCustomer: "等客戶回覆",
  WaitingInternal: "等內部處理",
  Resolved: "已解決",
  Closed: "已關閉",
};

const priorityLabels: Record<CustomerServiceTicketPriority, string> = {
  Low: "低",
  Normal: "一般",
  High: "高",
  Urgent: "緊急",
};

const categoryLabels: Record<CustomerServiceTicketCategory, string> = {
  General: "一般客服",
  Product: "商品諮詢",
  Reservation: "預約服務",
  Order: "訂單問題",
  Member: "會員問題",
  AfterSales: "售後服務",
};

const sourceTypeLabels: Record<CustomerServiceSourceType, string> = {
  Manual: "手動建立",
  ProductTrial: "試戴預約",
  Reservation: "一般預約",
  ContactMessage: "聯絡訊息",
  Order: "訂單",
  Member: "會員",
};

const activityTypeLabels: Record<CustomerServiceActivityType, string> = {
  Note: "內部備註",
  Call: "電話",
  Email: "Email",
  Customer: "會員提問",
  StatusChange: "狀態",
  Assignment: "指派",
  FollowUp: "追蹤",
  System: "系統",
};

const priorityClass: Record<CustomerServiceTicketPriority, string> = {
  Low: "bg-[#eef2f0] text-[#59665f]",
  Normal: "bg-[#e9f4ec] text-[#245438]",
  High: "bg-[#fff6df] text-[#8a6a2f]",
  Urgent: "bg-[#fdebea] text-[#9a2d2d]",
};

const statusClass: Record<CustomerServiceTicketStatus, string> = {
  New: "bg-[#eef2ff] text-[#3342a0]",
  InProgress: "bg-[#e9f4ec] text-[#245438]",
  WaitingCustomer: "bg-[#fff6df] text-[#8a6a2f]",
  WaitingInternal: "bg-[#f3ecdf] text-[#7a5610]",
  Resolved: "bg-[#e7f7f2] text-[#16705a]",
  Closed: "bg-[#eeeeee] text-[#59635d]",
};

const inputClass = "h-10 w-full rounded-md border border-[#cfd8d2] bg-white px-3 text-sm outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15";
const textareaClass = "min-h-[92px] w-full rounded-md border border-[#cfd8d2] bg-white px-3 py-2 text-sm outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15";

const emptyForm: CustomerServiceTicketPayload = {
  SourceType: "Manual",
  SourceId: "",
  Subject: "",
  Category: "General",
  Priority: "Normal",
  CustomerName: "",
  CustomerPhone: "",
  CustomerEmail: "",
  AssignedAdminUserId: "",
  NextFollowUpAt: "",
  InitialNote: "",
};

function formatDate(value: string | null) {
  if (!value) return "-";
  return value.slice(0, 16);
}

function isOverdue(ticket: CustomerServiceTicket) {
  if (!ticket.NextFollowUpAt || ticket.Status === "Closed" || ticket.Status === "Resolved") return false;
  return new Date(ticket.NextFollowUpAt).getTime() < Date.now();
}

export default function AdminCustomerServiceCenter() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [assignees, setAssignees] = useState<CustomerServiceAssignee[]>([]);
  const [tickets, setTickets] = useState<CustomerServiceTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<CustomerServiceTicket | null>(null);
  const [activities, setActivities] = useState<CustomerServiceActivity[]>([]);
  const [counts, setCounts] = useState<{ Status: Record<CustomerServiceTicketStatus, number>; Overdue: number } | null>(null);
  const [pagination, setPagination] = useState({ Page: 1, PerPage: 20, Total: 0, TotalPages: 1 });
  const [status, setStatus] = useState<CustomerServiceTicketStatus | "">("");
  const [priority, setPriority] = useState<CustomerServiceTicketPriority | "">("");
  const [sourceType, setSourceType] = useState<CustomerServiceSourceType | "">("");
  const [assigned, setAssigned] = useState("");
  const [keyword, setKeyword] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<CustomerServiceTicketPayload>(emptyForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [sourceKeyword, setSourceKeyword] = useState("");
  const [sourceOptions, setSourceOptions] = useState<CustomerServiceSourceOption[]>([]);
  const [selectedSourceOption, setSelectedSourceOption] = useState<CustomerServiceSourceOption | null>(null);
  const [sourceSearching, setSourceSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [noteType, setNoteType] = useState<CustomerServiceActivityType>("Note");
  const [noteBody, setNoteBody] = useState("");
  const [noteVisibleToCustomer, setNoteVisibleToCustomer] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);

  const filters = useMemo(() => ({
    Status: status,
    Priority: priority,
    SourceType: sourceType,
    AssignedAdminUserId: assigned,
    Keyword: keyword.trim(),
    Overdue: overdueOnly,
    Page: pagination.Page,
    PerPage: pagination.PerPage,
  }), [assigned, keyword, overdueOnly, pagination.Page, pagination.PerPage, priority, sourceType, status]);

  async function load(overrides: Partial<typeof filters> = {}) {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const [ticketData, userData] = await Promise.all([
        listCustomerServiceTickets({ ...filters, ...overrides }),
        listCustomerServiceAssignees(),
      ]);
      setTickets(ticketData.Tickets);
      setCounts(ticketData.Counts);
      setPagination(ticketData.Pagination);
      setAssignees(userData.Users);
      if (selectedTicket && !ticketData.Tickets.some((ticket) => ticket.TicketId === selectedTicket.TicketId)) {
        setSelectedTicket(null);
        setActivities([]);
      }
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "客服工單載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  async function openTicket(ticket: CustomerServiceTicket) {
    setSelectedTicket(ticket);
    setActivities([]);
    try {
      const data = await getCustomerServiceTicket(ticket.TicketId);
      setSelectedTicket(data.Ticket);
      setActivities(data.Activities);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "工單詳情載入失敗。");
    }
  }

  function openCreateForm() {
    setForm(emptyForm);
    setFormErrors({});
    setSourceKeyword("");
    setSourceOptions([]);
    setSelectedSourceOption(null);
    setFormOpen(true);
  }

  function changeCreateSourceType(value: CustomerServiceSourceType) {
    setForm((current) => ({ ...current, SourceType: value, SourceId: "" }));
    setSourceKeyword("");
    setSourceOptions([]);
    setSelectedSourceOption(null);
  }

  async function searchSourceOptions() {
    if (!form.SourceType || form.SourceType === "Manual") return;
    setSourceSearching(true);
    setFormErrors((current) => ({ ...current, SourceId: "" }));
    try {
      const data = await searchCustomerServiceSourceOptions(form.SourceType, sourceKeyword);
      setSourceOptions(data.Options);
      if (data.Options.length === 0) {
        setFormErrors((current) => ({ ...current, SourceId: "查無符合的來源資料，請換關鍵字或改用手動建立。" }));
      }
    } catch (error) {
      setFormErrors((current) => ({ ...current, SourceId: error instanceof Error ? error.message : "來源查詢失敗。" }));
    } finally {
      setSourceSearching(false);
    }
  }

  function selectSourceOption(option: CustomerServiceSourceOption) {
    setSelectedSourceOption(option);
    setForm((current) => ({
      ...current,
      SourceType: option.SourceType,
      SourceId: option.SourceId,
      CustomerName: current.CustomerName || option.CustomerName || "",
      CustomerPhone: current.CustomerPhone || option.CustomerPhone || "",
      CustomerEmail: current.CustomerEmail || option.CustomerEmail || "",
    }));
    setFormErrors((current) => ({ ...current, SourceId: "" }));
  }

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setFormErrors({});
    try {
      const payload = { ...form, SourceId: form.SourceId || null, AssignedAdminUserId: form.AssignedAdminUserId || null };
      const data = await createCustomerServiceTicket(payload);
      setFormOpen(false);
      await load({ Page: 1 });
      await openTicket(data.Ticket);
    } catch (error) {
      if (error instanceof AdminApiError) setFormErrors(error.errors);
      else setFormErrors({ Form: error instanceof Error ? error.message : "工單建立失敗。" });
    } finally {
      setSaving(false);
    }
  }

  async function updateTicket(payload: CustomerServiceTicketPayload) {
    if (!selectedTicket) return;
    try {
      const data = await updateCustomerServiceTicket(selectedTicket.TicketId, payload);
      setSelectedTicket(data.Ticket);
      setActivities(data.Activities);
      setTickets((current) => current.map((ticket) => ticket.TicketId === data.Ticket.TicketId ? data.Ticket : ticket));
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "工單更新失敗。");
    }
  }

  async function addActivity(event: FormEvent) {
    event.preventDefault();
    if (!selectedTicket || !noteBody.trim()) return;
    setNoteSaving(true);
    try {
      const data = await createCustomerServiceActivity(selectedTicket.TicketId, { ActivityType: noteType, Body: noteBody.trim(), Visibility: noteVisibleToCustomer ? "Customer" : "Internal" });
      setSelectedTicket(data.Ticket);
      setActivities(data.Activities);
      setNoteBody("");
      setNoteType("Note");
      setNoteVisibleToCustomer(false);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "處理紀錄新增失敗。");
    } finally {
      setNoteSaving(false);
    }
  }

  const activeTicket = selectedTicket;

  return (
    <AdminLayout sectionLabel="客服管理" title="客服中心" user={currentUser} onLogout={handleLogout}>
      <div className="mb-4 grid gap-3 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-center">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:max-w-[560px]">
          <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3">
            <p className="text-xs text-[#7b877f]">新工單</p>
            <p className="text-2xl font-bold text-[#203d32]">{counts?.Status.New || 0}</p>
          </div>
          <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3">
            <p className="text-xs text-[#7b877f]">處理中</p>
            <p className="text-2xl font-bold text-[#203d32]">{(counts?.Status.InProgress || 0) + (counts?.Status.WaitingCustomer || 0) + (counts?.Status.WaitingInternal || 0)}</p>
          </div>
          <button className="rounded-md border border-[#f0ddb0] bg-[#fffaf0] px-4 py-3 text-left" onClick={() => { setOverdueOnly(true); load({ Overdue: true, Page: 1 }); }} type="button">
            <p className="text-xs text-[#8a6a2f]">逾期追蹤</p>
            <p className="text-2xl font-bold text-[#8a6a2f]">{counts?.Overdue || 0}</p>
          </button>
          <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3">
            <p className="text-xs text-[#7b877f]">已解決</p>
            <p className="text-2xl font-bold text-[#203d32]">{counts?.Status.Resolved || 0}</p>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row xl:justify-end">
          <button className="inline-flex h-10 items-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => load()} type="button">
            <RefreshCw size={17} /> 重新整理
          </button>
          <button className="inline-flex h-10 items-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white hover:bg-[#203d32]" onClick={openCreateForm} type="button">
            <Plus size={18} /> 新增工單
          </button>
        </div>
      </div>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_440px]">
        <section className="min-w-0 rounded-md border border-[#dfe6e1] bg-white">
          <div className="flex flex-wrap items-center gap-3 border-b border-[#edf0ee] p-4">
            <label className="relative min-w-[240px] flex-[1_1_280px]">
              <Search className="absolute left-3 top-2.5 text-[#9aa59f]" size={16} />
              <input className={`${inputClass} pl-9`} placeholder="工單編號、主旨、姓名、電話、Email" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
            </label>
            <select className={`${inputClass} shrink-0 sm:!w-[150px]`} value={status} onChange={(event) => setStatus(event.target.value as CustomerServiceTicketStatus | "")}>
              <option value="">全部狀態</option>
              {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <select className={`${inputClass} shrink-0 sm:!w-[150px]`} value={priority} onChange={(event) => setPriority(event.target.value as CustomerServiceTicketPriority | "")}>
              <option value="">全部優先度</option>
              {Object.entries(priorityLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <select className={`${inputClass} shrink-0 sm:!w-[150px]`} value={sourceType} onChange={(event) => setSourceType(event.target.value as CustomerServiceSourceType | "")}>
              <option value="">全部來源</option>
              {Object.entries(sourceTypeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <select className={`${inputClass} shrink-0 sm:!w-[150px]`} value={assigned} onChange={(event) => setAssigned(event.target.value)}>
              <option value="">全部指派</option>
              <option value="Unassigned">未指派</option>
              {assignees.map((user) => <option key={user.AdminUserId} value={user.AdminUserId}>{user.DisplayName || user.Username}</option>)}
            </select>
            <label className="flex h-10 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#cfd8d2] px-3 text-sm font-bold leading-tight text-[#315746] sm:w-[128px]">
              <input checked={overdueOnly} onChange={(event) => setOverdueOnly(event.target.checked)} type="checkbox" />
              只看逾期
            </label>
            <button className="h-10 w-full rounded-md bg-[#315746] px-4 font-bold text-white hover:bg-[#203d32] sm:w-[90px]" onClick={() => load({ Page: 1 })} type="button">查詢</button>
          </div>

          <div className="divide-y divide-[#edf0ee]">
            {loading ? (
              <p className="p-8 text-center text-[#7b877f]">載入客服工單中...</p>
            ) : tickets.length === 0 ? (
              <p className="p-8 text-center text-[#7b877f]">目前沒有符合條件的客服工單。</p>
            ) : tickets.map((ticket) => (
              <button
                className={`grid w-full gap-3 p-4 text-left transition hover:bg-[#f7faf8] lg:grid-cols-[minmax(240px,.8fr)_140px_210px_128px] ${activeTicket?.TicketId === ticket.TicketId ? "bg-[#f0f5f2]" : ""}`}
                key={ticket.TicketId}
                onClick={() => openTicket(ticket)}
                type="button"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-bold text-[#203d32]">{ticket.TicketNumber}</span>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${priorityClass[ticket.Priority]}`}>{priorityLabels[ticket.Priority]}</span>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${statusClass[ticket.Status]}`}>{statusLabels[ticket.Status]}</span>
                    {isOverdue(ticket) && <span className="rounded-full bg-[#fdebea] px-2.5 py-1 text-xs font-bold text-[#9a2d2d]">追蹤逾期</span>}
                  </div>
                  <p className="mt-1 truncate text-sm font-bold text-[#203d32]">{ticket.Subject}</p>
                  <p className="mt-1 truncate text-xs text-[#7b877f]">{sourceTypeLabels[ticket.SourceType]}{ticket.SourceId ? ` #${ticket.SourceId}` : ""}</p>
                </div>
                <div className="text-sm text-[#59665f]">
                  <p className="font-bold text-[#203d32]">{ticket.CustomerName || "-"}</p>
                  <p className="truncate text-xs">{ticket.CustomerPhone || ticket.CustomerEmail || "-"}</p>
                </div>
                <div className="whitespace-nowrap text-xs text-[#7b877f] [&>p:nth-child(n+3)]:hidden">
                  <p className="truncate">指派：{ticket.AssignedAdminName || "未指派"}</p>
                  <p className="truncate">更新：{formatDate(ticket.UpdatedAt)}</p>
                  <p>指派：{ticket.AssignedAdminName || "未指派"}</p>
                  <p>更新：{formatDate(ticket.UpdatedAt)}</p>
                </div>
                <div className="text-xs text-[#7b877f]">
                  <p className="flex items-center gap-1"><CalendarClock size={13} /> {formatDate(ticket.NextFollowUpAt)}</p>
                </div>
              </button>
            ))}
          </div>

          <div className="border-t border-[#edf0ee] p-4">
            <Pagination pagination={pagination} onChange={(Page) => load({ Page })} />
          </div>
        </section>

        <aside className="rounded-md border border-[#dfe6e1] bg-white xl:sticky xl:top-20 xl:max-h-[calc(100vh-96px)] xl:overflow-y-auto">
          {!activeTicket ? (
            <div className="flex min-h-[420px] flex-col items-center justify-center p-6 text-center text-[#7b877f]">
              <MessageSquarePlus className="mb-3 text-[#9aa59f]" size={36} />
              <p className="font-bold text-[#203d32]">選擇一張工單</p>
              <p className="mt-1 text-sm">客服處理紀錄、指派與追蹤時間會顯示在這裡。</p>
            </div>
          ) : (
            <div>
              <div className="border-b border-[#edf0ee] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold text-[#7b877f]">{activeTicket.TicketNumber}</p>
                    <h2 className="mt-1 text-lg font-bold text-[#203d32]">{activeTicket.Subject}</h2>
                  </div>
                  <button className="xl:hidden" onClick={() => setSelectedTicket(null)} type="button"><X size={20} /></button>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <span className={`rounded-full px-2.5 py-1 font-bold ${priorityClass[activeTicket.Priority]}`}>{priorityLabels[activeTicket.Priority]}</span>
                  <span className={`rounded-full px-2.5 py-1 font-bold ${statusClass[activeTicket.Status]}`}>{statusLabels[activeTicket.Status]}</span>
                </div>
              </div>

              <div className="grid gap-4 p-4">
                <section className="grid gap-2 rounded-md bg-[#f8fbf9] p-3 text-sm">
                  <p className="font-bold text-[#203d32]">客戶資料</p>
                  <p className="flex items-center gap-2"><UserRound size={15} /> {activeTicket.CustomerName || "-"}</p>
                  <p className="flex items-center gap-2"><Phone size={15} /> {activeTicket.CustomerPhone || "-"}</p>
                  <p className="flex items-center gap-2"><Mail size={15} /> {activeTicket.CustomerEmail || "-"}</p>
                  <p className="text-xs text-[#7b877f]">來源：{sourceTypeLabels[activeTicket.SourceType]}{activeTicket.SourceId ? ` #${activeTicket.SourceId}` : ""}</p>
                </section>

                <section className="grid gap-3">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <label className="grid gap-1 text-xs font-bold text-[#315746]">
                      狀態
                      <select className={inputClass} value={activeTicket.Status} onChange={(event) => updateTicket({ Status: event.target.value as CustomerServiceTicketStatus })}>
                        {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                      </select>
                    </label>
                    <label className="grid gap-1 text-xs font-bold text-[#315746]">
                      優先度
                      <select className={inputClass} value={activeTicket.Priority} onChange={(event) => updateTicket({ Priority: event.target.value as CustomerServiceTicketPriority })}>
                        {Object.entries(priorityLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                      </select>
                    </label>
                    <label className="grid gap-1 text-xs font-bold text-[#315746]">
                      類別
                      <select className={inputClass} value={activeTicket.Category} onChange={(event) => updateTicket({ Category: event.target.value as CustomerServiceTicketCategory })}>
                        {Object.entries(categoryLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                      </select>
                    </label>
                    <label className="grid gap-1 text-xs font-bold text-[#315746]">
                      指派客服
                      <select className={inputClass} value={activeTicket.AssignedAdminUserId || ""} onChange={(event) => updateTicket({ AssignedAdminUserId: event.target.value || null })}>
                        <option value="">未指派</option>
                        {assignees.map((user) => <option key={user.AdminUserId} value={user.AdminUserId}>{user.DisplayName || user.Username}</option>)}
                      </select>
                    </label>
                    <label className="grid gap-1 text-xs font-bold text-[#315746] sm:col-span-2">
                      下次追蹤
                      <input className={inputClass} type="datetime-local" value={activeTicket.NextFollowUpAt ? activeTicket.NextFollowUpAt.slice(0, 16).replace(" ", "T") : ""} onChange={(event) => updateTicket({ NextFollowUpAt: event.target.value || null })} />
                    </label>
                  </div>
                </section>

                <form className="grid gap-2 rounded-md border border-[#dfe6e1] p-3" onSubmit={addActivity}>
                  <div className="flex gap-2">
                    <select className={`${inputClass} max-w-[130px]`} value={noteType} onChange={(event) => setNoteType(event.target.value as CustomerServiceActivityType)}>
                      {(["Note", "Call", "Email", "FollowUp"] as CustomerServiceActivityType[]).map((value) => <option key={value} value={value}>{activityTypeLabels[value]}</option>)}
                    </select>
                    <button className="inline-flex h-10 shrink-0 items-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white disabled:opacity-60" disabled={noteSaving || !noteBody.trim()} type="submit">
                      <Save size={16} /> 儲存
                    </button>
                  </div>
                  <textarea className={textareaClass} placeholder="輸入電話紀錄、Email 摘要、內部備註或下次追蹤事項。" value={noteBody} onChange={(event) => setNoteBody(event.target.value)} />
                  <button
                    aria-pressed={noteVisibleToCustomer}
                    className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-left transition ${
                      noteVisibleToCustomer
                        ? "border-[#bdd3c6] bg-[#e9f4ec] text-[#203d32]"
                        : "border-[#dfe6e1] bg-[#f8fbf9] text-[#59665f]"
                    }`}
                    onClick={() => setNoteVisibleToCustomer((current) => !current)}
                    type="button"
                  >
                    <span>
                      <span className="block text-sm font-bold">{noteVisibleToCustomer ? "公開回覆會員" : "內部備註"}</span>
                      <span className="mt-0.5 block text-xs">{noteVisibleToCustomer ? "儲存後會同步顯示在官網會員中心。" : "只留在後台，會員不會看到這則紀錄。"}</span>
                    </span>
                    <span className={`relative h-6 w-11 shrink-0 rounded-full transition ${noteVisibleToCustomer ? "bg-[#315746]" : "bg-[#cfd8d2]"}`}>
                      <span className={`absolute top-1 h-4 w-4 rounded-full bg-white transition ${noteVisibleToCustomer ? "left-6" : "left-1"}`} />
                    </span>
                  </button>
                  <label className="hidden items-center gap-2 text-xs font-bold text-[#315746]">
                    <input checked={noteVisibleToCustomer} onChange={(event) => setNoteVisibleToCustomer(event.target.checked)} type="checkbox" />
                    公開給會員查看
                  </label>
                </form>

                <section>
                  <h3 className="mb-2 flex items-center gap-2 text-sm font-bold text-[#203d32]"><Clock size={16} /> 處理紀錄</h3>
                  <div className="space-y-3">
                    {activities.length === 0 ? (
                      <p className="rounded-md bg-[#f8fbf9] p-4 text-sm text-[#7b877f]">尚無處理紀錄。</p>
                    ) : activities.map((activity) => (
                      <div className="rounded-md border border-[#edf0ee] p-3" key={activity.ActivityId}>
                        <div className="flex items-center justify-between gap-2 text-xs text-[#7b877f]">
                          <span className="font-bold text-[#315746]">{activityTypeLabels[activity.ActivityType]}</span>
                          <span>{formatDate(activity.CreatedAt)}</span>
                        </div>
                        <p className="mt-1 whitespace-pre-wrap text-sm text-[#203d32]">{activity.Body}</p>
                        <p className="mt-2 text-xs text-[#9aa59f]">{activity.AdminDisplayName || "系統"}</p>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </div>
          )}
        </aside>
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-[#10251c]/40 sm:items-center">
          <button className="absolute inset-0 cursor-default" onClick={() => setFormOpen(false)} type="button" />
          <form className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-lg bg-white shadow-2xl sm:rounded-md" onSubmit={handleCreate}>
            <div className="flex items-start justify-between gap-4 border-b border-[#edf0ee] px-5 py-4">
              <div>
                <h2 className="text-xl font-bold text-[#203d32]">新增客服工單</h2>
                <p className="mt-1 text-sm text-[#7b877f]">可手動建立，也可輸入來源類型與來源 ID 帶入客戶資料。</p>
              </div>
              <button className="rounded-md border border-[#cfd8d2] p-2 text-[#315746]" onClick={() => setFormOpen(false)} type="button"><X size={18} /></button>
            </div>
            <div className="grid gap-4 overflow-y-auto px-5 py-5">
              {formErrors.Form && <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{formErrors.Form}</div>}
              <div className="grid gap-4">
                <label className="grid gap-1 text-sm font-bold text-[#315746]">
                  來源
                  <select className={inputClass} value={form.SourceType} onChange={(event) => changeCreateSourceType(event.target.value as CustomerServiceSourceType)}>
                    {Object.entries(sourceTypeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </label>
                {form.SourceType !== "Manual" && (
                  <div className="grid gap-2 rounded-md border border-[#dfe6e1] bg-[#f8fbf9] p-3">
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <label className="relative flex-1">
                        <Search className="absolute left-3 top-2.5 text-[#9aa59f]" size={16} />
                        <input
                          className={`${inputClass} pl-9`}
                          placeholder="輸入姓名、電話、Email、訂單號、商品或門市"
                          value={sourceKeyword}
                          onChange={(event) => setSourceKeyword(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              searchSourceOptions();
                            }
                          }}
                        />
                      </label>
                      <button className="h-10 rounded-md bg-[#315746] px-4 font-bold text-white disabled:opacity-60" disabled={sourceSearching} onClick={searchSourceOptions} type="button">
                        {sourceSearching ? "查詢中..." : "搜尋來源"}
                      </button>
                    </div>
                    {formErrors.SourceId && <span className="text-xs font-medium text-[#b3261e]">{formErrors.SourceId}</span>}
                    {selectedSourceOption && (
                      <div className="rounded-md border border-[#bdd3c6] bg-white px-3 py-2 text-sm">
                        <p className="font-bold text-[#203d32]">已選擇：{selectedSourceOption.Label}</p>
                        <p className="mt-1 text-xs text-[#7b877f]">{selectedSourceOption.Description}</p>
                      </div>
                    )}
                    {sourceOptions.length > 0 && (
                      <div className="max-h-56 overflow-y-auto rounded-md border border-[#dfe6e1] bg-white">
                        {sourceOptions.map((option) => (
                          <button
                            className={`block w-full border-b border-[#edf0ee] px-3 py-2 text-left text-sm last:border-b-0 hover:bg-[#f0f5f2] ${form.SourceId === option.SourceId ? "bg-[#e9f4ec]" : ""}`}
                            key={`${option.SourceType}-${option.SourceId}`}
                            onClick={() => selectSourceOption(option)}
                            type="button"
                          >
                            <span className="font-bold text-[#203d32]">{option.Label}</span>
                            <span className="mt-1 block text-xs text-[#7b877f]">{option.Description}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <label className="grid gap-1 text-sm font-bold text-[#315746]">
                主旨
                <input className={inputClass} value={form.Subject || ""} onChange={(event) => setForm((current) => ({ ...current, Subject: event.target.value }))} />
                {formErrors.Subject && <span className="text-xs text-[#b3261e]">{formErrors.Subject}</span>}
              </label>
              <div className="grid gap-4 sm:grid-cols-3">
                <label className="grid gap-1 text-sm font-bold text-[#315746]">
                  類別
                  <select className={inputClass} value={form.Category} onChange={(event) => setForm((current) => ({ ...current, Category: event.target.value as CustomerServiceTicketCategory }))}>
                    {Object.entries(categoryLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </label>
                <label className="grid gap-1 text-sm font-bold text-[#315746]">
                  優先度
                  <select className={inputClass} value={form.Priority} onChange={(event) => setForm((current) => ({ ...current, Priority: event.target.value as CustomerServiceTicketPriority }))}>
                    {Object.entries(priorityLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </label>
                <label className="grid gap-1 text-sm font-bold text-[#315746]">
                  指派
                  <select className={inputClass} value={form.AssignedAdminUserId || ""} onChange={(event) => setForm((current) => ({ ...current, AssignedAdminUserId: event.target.value }))}>
                    <option value="">未指派</option>
                    {assignees.map((user) => <option key={user.AdminUserId} value={user.AdminUserId}>{user.DisplayName || user.Username}</option>)}
                  </select>
                </label>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <label className="grid gap-1 text-sm font-bold text-[#315746]">
                  客戶姓名
                  <input className={inputClass} value={form.CustomerName || ""} onChange={(event) => setForm((current) => ({ ...current, CustomerName: event.target.value }))} />
                </label>
                <label className="grid gap-1 text-sm font-bold text-[#315746]">
                  電話
                  <input className={inputClass} value={form.CustomerPhone || ""} onChange={(event) => setForm((current) => ({ ...current, CustomerPhone: event.target.value }))} />
                </label>
                <label className="grid gap-1 text-sm font-bold text-[#315746]">
                  Email
                  <input className={inputClass} value={form.CustomerEmail || ""} onChange={(event) => setForm((current) => ({ ...current, CustomerEmail: event.target.value }))} />
                </label>
              </div>
              {formErrors.Customer && <p className="text-sm font-medium text-[#b3261e]">{formErrors.Customer}</p>}
              <label className="grid gap-1 text-sm font-bold text-[#315746]">
                下次追蹤
                <input className={inputClass} type="datetime-local" value={form.NextFollowUpAt || ""} onChange={(event) => setForm((current) => ({ ...current, NextFollowUpAt: event.target.value }))} />
              </label>
              <label className="grid gap-1 text-sm font-bold text-[#315746]">
                初始內部備註
                <textarea className={textareaClass} value={form.InitialNote || ""} onChange={(event) => setForm((current) => ({ ...current, InitialNote: event.target.value }))} />
              </label>
            </div>
            <div className="flex justify-end gap-3 border-t border-[#edf0ee] px-5 py-4">
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={() => setFormOpen(false)} type="button">取消</button>
              <button className="inline-flex h-10 items-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={saving} type="submit">
                <CheckCircle2 size={17} /> {saving ? "建立中..." : "建立工單"}
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
